import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, ArrowLeft, Users, Calendar, AlertCircle, Play, Loader2, Search, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import VideoPlayer from '../ui/VideoPlayer';
import { API_BASE_URL } from '../../config/api';

// List of admin UIDs
const ADMIN_UIDS = [
  "iMTsoOPrg6eR4rlJmaL1f0pA07Y2"
  // Add other admin UIDs here as needed
];

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser, getIdToken } = useAuth();
  const { credits, fetchUserCredits } = useCredits();
  
  const [event, setEvent] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableAnalyses, setAvailableAnalyses] = useState([]);
  const [filteredAnalyses, setFilteredAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isEntering, setIsEntering] = useState(false);
  const [showEnterModal, setShowEnterModal] = useState(false);
  const [isEligible, setIsEligible] = useState(true);
  const [eligibilityMessage, setEligibilityMessage] = useState('');
  const [userCountry, setUserCountry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Refresh entries every 10 seconds
  useEffect(() => {
    fetchEventDetails();
    const interval = setInterval(fetchEventDetails, 10000);
    return () => clearInterval(interval);
  }, [eventId]);

  useEffect(() => {
    if (currentUser) {
      // Check if user is admin
      setIsAdmin(ADMIN_UIDS.includes(currentUser.uid));
      fetchUserAnalyses();
      checkUserEligibility();
    }
  }, [currentUser, event]);

  // Filter analyses when search term changes
  useEffect(() => {
    filterAnalyses();
  }, [searchTerm, availableAnalyses, event]);

  const fetchEventDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/entries`);
      if (!response.ok) throw new Error('Failed to fetch event details');
      
      const data = await response.json();
      setEvent(data.event);
      setEntries(data.entries);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAnalyses = async () => {
    try {
      const token = await getIdToken();

      //console.log("TOKEN: ",token)
      
      // Get the event's dance type or analysis type
      // If the event has a specific analysis_type requirement, use that
      const requiredAnalysisType = event.analysis_type || "couple"; // Default to couple if not specified
      
      // Update API call to include analysis_type
      const response = await fetch(`${API_BASE_URL}/analyses?user_id=${currentUser.uid}&analysis_type=${requiredAnalysisType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch analyses');
      
      const data = await response.json();
      //console.log(data)
      // Filter out analyses that are already entered in this event
      const enteredAnalysisIds = entries.map(entry => entry.analysis_id);
      const available = data.filter(analysis => 
        analysis.processed && 
        analysis.total_score &&
        !enteredAnalysisIds.includes(analysis.id)
      );
      //console.log(available)
      setAvailableAnalyses(available);
      filterAnalyses(available);
    } catch (err) {
      console.error('Error fetching analyses:', err);
    }
  };

  const filterAnalyses = (analyses = availableAnalyses) => {
    if (!analyses || !event) return;
  
    // Start with all analyses
    let filtered = analyses;
    
    // Filter by dance type if event has a specific dance type
    if (event.dance_type && event.dance_type !== 'all') {
      const eventDanceType = event.dance_type.toUpperCase();
      filtered = filtered.filter(analysis => {
        const analysisDanceType = analysis.dance_type ? analysis.dance_type.toUpperCase() : '';
        return analysisDanceType === eventDanceType;
      });
    }
    
    // Filter by analysis type if event has a specific analysis type
    if (event.analysis_type) {
      filtered = filtered.filter(analysis => {
        // Default to 'couple' if analysis_type is not set
        const analysisType = analysis.analysis_type || 'couple';
        return analysisType === event.analysis_type;
      });
    }
    
    // Apply search filter if search term exists
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(analysis => 
        (analysis.dance_type && analysis.dance_type.toLowerCase().includes(term)) ||
        (analysis.dancers && analysis.dancers.toLowerCase().includes(term))
      );
    }
    
    // DEBUG LOGS - remove in production
    console.log('Event dance type:', event.dance_type);
    console.log('Event analysis type:', event.analysis_type);
    console.log('Available analyses:', analyses);
    console.log('Filtered analyses:', filtered);
    
    setFilteredAnalyses(filtered);
  };

  const checkUserEligibility = async () => {
    if (!currentUser || !event) return;

    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/auth/user-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        const country = userData.country;
        setUserCountry(country);
        
        // Check if event has country restrictions
        if (event.country_restriction && event.allowed_countries && !event.allowed_countries.includes('all')) {
          if (!event.allowed_countries.includes(country)) {
            setIsEligible(false);
            setEligibilityMessage(`This event is restricted to participants from: ${event.allowed_countries.join(', ')}. Your country (${country}) is not eligible.`);
          } else {
            setIsEligible(true);
            setEligibilityMessage('');
          }
        } else {
          setIsEligible(true);
          setEligibilityMessage('');
        }
      }
    } catch (error) {
      console.error('Error checking eligibility:', error);
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This will remove all entries and cannot be undone.')) {
      return;
    }
    
    try {
      setIsDeleting(true);
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete event');
      }
      
      // Navigate back to events list after successful deletion
      navigate('/events');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEnterEvent = async () => {
    if (!selectedAnalysis) return;
    
    try {
      setIsEntering(true);
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/enter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          analysis_id: selectedAnalysis
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to enter event');
      }
      
      // Refresh data
      await fetchEventDetails();
      await fetchUserAnalyses();
      await fetchUserCredits();
      setShowEnterModal(false);
      setSelectedAnalysis(null);
      setSearchTerm('');
    } catch (err) {
      alert(err.message);
    } finally {
      setIsEntering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <button
        onClick={() => navigate('/events')}
        className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Events
      </button>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {event && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{event.name}</h1>
              <p className="text-gray-600">{event.description}</p>
              
              {/* Dance Type Indicator */}
              {event.dance_type && event.dance_type !== 'all' && (
                <div className="mt-2 inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {event.dance_type}
                </div>
              )}
              
              {/* Country Restrictions Display */}
              {event.country_restriction && event.allowed_countries && !event.allowed_countries.includes('all') && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Country Restrictions
                  </h4>
                  <p className="text-sm text-yellow-700">
                    This event is restricted to participants from: 
                    <span className="font-medium"> {event.allowed_countries.join(', ')}</span>
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex flex-col items-end gap-2">
              {/* Admin Delete Button */}
              {isAdmin && (
                <button
                  onClick={handleDeleteEvent}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                >
                  {isDeleting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Delete Event
                </button>
              )}
              
              {currentUser && credits >= 2 && isEligible && (
                <button
                  onClick={() => setShowEnterModal(true)}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Enter Event (2 credits)
                </button>
              )}
              
              {currentUser && !isEligible && (
                <div className="px-4 py-2 bg-red-100 text-red-700 rounded-lg">
                  {eligibilityMessage}
                </div>
              )}
              
              {currentUser && credits < 2 && (
                <div className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg">
                  Insufficient credits. You need 2 credits to enter.
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-3 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Start Date</p>
                <p className="font-medium">{new Date(event.start_date).toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-3 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">End Date</p>
                <p className="font-medium">{new Date(event.end_date).toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-3 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Entries</p>
                <p className="font-medium">{entries.length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center">
            <Trophy className="w-5 h-5 mr-2 text-yellow-500" />
            Leaderboard
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Video</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dance Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dancers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr key={entry.id} className={entry.rank === 1 ? 'bg-yellow-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {entry.rank === 1 && <Trophy className="w-5 h-5 text-yellow-500 mr-2" />}
                      <span className="text-sm font-medium text-gray-900">#{entry.rank}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-32">
                      <VideoPlayer videoUrl={entry.video_url} className="w-full" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.dance_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.dancers}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.user_email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.user_country}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {entry.total_score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {entries.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Entries Yet</h3>
            <p className="text-gray-500">Be the first to enter this event!</p>
          </div>
        )}
      </div>

      {/* Enter Event Modal */}
      {showEnterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Enter Event</h3>
              <button 
                onClick={() => {
                  setShowEnterModal(false);
                  setSelectedAnalysis(null);
                  setSearchTerm('');
                }} 
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            {/* Dance type filter notice */}
            {event && event.dance_type && event.dance_type !== 'all' && (
              <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">
                This event is specifically for <strong>{event.dance_type}</strong>. 
                Only analyses of this dance type are shown.
              </div>
            )}

            {/* Analysis Search */}
            <div className="relative mb-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search analyses by dance type or dancers..."
                className="w-full p-2 pl-10 border rounded-lg"
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>

            {filteredAnalyses.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
                <p className="text-gray-700 mb-4">
                  {searchTerm 
                    ? 'No analyses match your search criteria.' 
                    : event.dance_type !== 'all' 
                      ? `You don't have any ${event.dance_type} analyses to enter.`
                      : "You don't have any eligible analyses to enter."}
                </p>
                <p className="text-sm text-gray-500">
                  {event.dance_type !== 'all' 
                    ? `Complete a ${event.dance_type} analysis first, then come back to enter the event.`
                    : "Complete an analysis first, then come back to enter the event."}
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Go to Analyses
                </button>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-4">Select an analysis to enter into the event:</p>
                
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredAnalyses.map(analysis => (
                    <div
                      key={analysis.id}
                      onClick={() => setSelectedAnalysis(analysis.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition ${
                        selectedAnalysis === analysis.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{analysis.dance_type}</p>
                          <p className="text-sm text-gray-600">{analysis.dancers}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(analysis.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                          Score: {analysis.total_score || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEnterModal(false);
                      setSelectedAnalysis(null);
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEnterEvent}
                    disabled={!selectedAnalysis || isEntering}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isEntering ? (
                      <span className="flex items-center">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Entering...
                      </span>
                    ) : (
                      'Enter Event (2 credits)'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;